// src/components/publishing/draft-composer.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Sparkles, Loader2, FileCode2, Globe, Linkedin, AlertCircle, Settings2,
} from "lucide-react";
import {
  Destination, DESTINATIONS, destinationLabel, Integration,
} from "@/app/services/publishing/publishing-api";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { getPublisherContext } from "@/api/content";
import type { PublisherContext } from "@/api/content";

interface DraftComposerProps {
  integrations: Integration[];
  brandName?: string;
  onDraft: (params: {
    brief: string;
    destinations: Destination[];
    tone?: string;
    /** #132: write from this action's brief (null: a free-text brief). */
    actionId?: number | null;
    customerId?: number | null;
  }) => Promise<void>;
  drafting: boolean;
  /**
   * Called when the user clicks the "Go to Integrations" CTA (shown when
   * one or more selected destinations aren't connected yet). The parent
   * should route the user to the Integrations page.
   */
  onNavigateToIntegrations?: () => void;
}

const TONE_PRESETS = [
  { id: "",            label: "Default (brand voice)" },
  { id: "analytical",  label: "Analytical" },
  { id: "punchy",      label: "Punchy & conversational" },
  { id: "authoritative", label: "Authoritative & thought-leadery" },
  { id: "story",       label: "Story-driven" },
];

export function DraftComposer({
  integrations, brandName, onDraft, drafting, onNavigateToIntegrations,
}: DraftComposerProps) {
  const [brief, setBrief] = useState("");
  const [selected, setSelected] = useState<Set<Destination>>(new Set());
  const [tone, setTone] = useState<string>("");
  // #132: the composer starts from a brief. An action's brief carries the
  // evidence; free text is still allowed, marked "no action, no evidence".
  const [context, setContext] = useState<PublisherContext | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);

  useEffect(() => {
    setContext(null);
    setActionId(null);
    if (!brandName) return;
    getPublisherContext(brandName).then(setContext).catch(() => setContext(null));
  }, [brandName]);

  const connectedDestinations = useMemo(
    () => new Set(integrations.filter(i => i.status === "active").map(i => i.destination)),
    [integrations],
  );

  const canDraft = brief.trim().length >= 5 && selected.size > 0 && !drafting;

  const toggle = (d: Destination) => {
    const next = new Set(selected);
    if (next.has(d)) next.delete(d); else next.add(d);
    setSelected(next);
  };

  const handleDraft = async () => {
    if (!canDraft) return;
    await onDraft({
      brief: brief.trim(),
      destinations: Array.from(selected),
      tone: tone || undefined,
      actionId,
      customerId: context?.customer_id ?? null,
    });
  };

  const disconnectedSelected = Array.from(selected).filter(d => !connectedDestinations.has(d));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{
        padding: "10px 12px", borderRadius: 9,
        backgroundColor: "rgba(13,148,136,0.04)",
        border: "1px solid rgba(13,148,136,0.15)",
        display: "flex", alignItems: "flex-start", gap: 10,
      }}>
        <Sparkles style={{
          width: 16, height: 16, color: "#0D9488",
          flexShrink: 0, marginTop: 1,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: "var(--foreground)",
            marginBottom: 2,
          }}>
            Generate with AI
          </div>
          <div style={{ fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
            Describe what you want to publish. Claude drafts one version per
            destination, adapting length and format for each channel. You can
            review and edit everything before it goes live.
          </div>
        </div>
      </div>

      <div>
        <Label>Start from</Label>
        {context && context.customer_id != null && context.actions.length > 0 ? (
          <select
            value={actionId ?? ""}
            onChange={e => {
              const id = e.target.value ? Number(e.target.value) : null;
              setActionId(id);
              const picked = context.actions.find(a => a.id === id);
              if (picked && !brief.trim()) setBrief(picked.title);
            }}
            disabled={drafting}
            style={{
              width: "100%", padding: "8px 10px", borderRadius: 9, border: "1px solid var(--border)",
              backgroundColor: "var(--background)", fontSize: 13, color: "var(--foreground)",
            }}
          >
            <option value="">A free-text brief (no action, no evidence)</option>
            {context.actions.map(a => (
              <option key={a.id} value={a.id}>{a.title}{a.belief ? ` · ${a.belief}` : ""}</option>
            ))}
          </select>
        ) : null}
        <p style={{ fontSize: 11, color: actionId ? "var(--muted-foreground)" : "#B45309", marginTop: 4 }}>
          {context?.customer_id == null
            ? (context?.note || "This brand has no brand record: drafts are written without evidence and their claims are not checked.")
            : actionId
              ? "Written from this action's brief: the belief's figures, AI quotes, confirmed facts and voice. Every claim is checked against the brand record."
              : "No action, no evidence. The brand record's facts and voice still apply, and every claim is checked against it."}
        </p>
      </div>

      <div>
        <Label>What do you want to publish about?</Label>
        <textarea
          value={brief}
          onChange={e => setBrief(e.target.value)}
          placeholder={
            brandName
              ? `e.g. "Announce our new integration with Salesforce. Focus on why we built it and what customers can automate now."`
              : `e.g. "Launch post for our new feature..."`
          }
          rows={5}
          style={{
            width: "100%", padding: "10px 12px", borderRadius: 9,
            border: "1px solid var(--border)",
            backgroundColor: "var(--background)",
            fontSize: 13, lineHeight: 1.55, color: "var(--foreground)",
            outline: "none", boxSizing: "border-box",
            fontFamily: "inherit", resize: "vertical",
          }}
          disabled={drafting}
        />
        <p style={{ fontSize: 11, color: "var(--muted-foreground)", marginTop: 4 }}>
          Be specific about the angle. The AI will adapt length and format per destination.
        </p>
      </div>

      <div>
        <Label>Destinations</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {DESTINATIONS.map(d => (
            <DestinationCheckbox
              key={d}
              destination={d}
              checked={selected.has(d)}
              connected={connectedDestinations.has(d)}
              onToggle={() => toggle(d)}
            />
          ))}
        </div>
        {disconnectedSelected.length > 0 && (
          <div className="mt-2">
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>
                {disconnectedSelected.length === 1
                  ? `${destinationLabel(disconnectedSelected[0])} isn't connected`
                  : "Some destinations aren't connected"}
              </AlertTitle>
              <AlertDescription>
                <div style={{ marginBottom: 8 }}>
                  You can still draft content, but publishing will fail for
                  destinations that aren't connected. Connect them from the
                  Integrations page first.
                </div>
                {onNavigateToIntegrations && (
                  <button
                    onClick={onNavigateToIntegrations}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "5px 10px", borderRadius: 6,
                      border: "1px solid currentColor",
                      backgroundColor: "transparent",
                      color: "inherit",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    <Settings2 style={{ width: 11, height: 11 }} />
                    Go to Integrations
                  </button>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>

      <div>
        <Label>Tone (optional)</Label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TONE_PRESETS.map(t => {
            const active = tone === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTone(t.id)}
                style={{
                  padding: "6px 11px", borderRadius: 999,
                  border: `1px solid ${active ? "#0D9488" : "var(--border)"}`,
                  backgroundColor: active ? "rgba(13,148,136,0.08)" : "var(--muted)",
                  color: active ? "#0D9488" : "var(--muted-foreground)",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                }}
                disabled={drafting}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{
        display: "flex", gap: 10, justifyContent: "flex-end",
        flexWrap: "wrap", paddingTop: 4,
      }}>
        <button
          onClick={handleDraft}
          disabled={!canDraft}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "10px 18px", borderRadius: 9,
            border: "none",
            backgroundColor: canDraft ? "#0D9488" : "var(--border)",
            color: "white",
            cursor: canDraft ? "pointer" : "not-allowed",
            fontSize: 13, fontWeight: 600,
          }}
        >
          {drafting
            ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            : <Sparkles style={{ width: 14, height: 14 }} />
          }
          {drafting ? "Drafting…" : "Draft content with AI"}
        </button>
      </div>
    </div>
  );
}

// ─── Destination checkbox row ────────────────────────────────────────────────

function DestinationCheckbox({
  destination, checked, connected, onToggle,
}: {
  destination: Destination;
  checked: boolean;
  connected: boolean;
  onToggle: () => void;
}) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF", blurb: "Long-form article" },
    wordpress: { Icon: Globe,     color: "#21759B", blurb: "Blog post (HTML)" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2", blurb: "Short-form post" },
  }[destination];

  return (
    <button
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 11px", borderRadius: 9,
        border: `1px solid ${checked ? "#0D9488" : "var(--border)"}`,
        backgroundColor: checked ? "rgba(13,148,136,0.06)" : "var(--muted)",
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div
        style={{
          width: 16, height: 16, borderRadius: 4,
          border: `2px solid ${checked ? "#0D9488" : "var(--border)"}`,
          backgroundColor: checked ? "#0D9488" : "transparent",
          flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      <div style={{
        width: 28, height: 28, borderRadius: 7,
        backgroundColor: "var(--background)", border: "1px solid var(--border)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <meta.Icon style={{ width: 14, height: 14, color: meta.color }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
          {destinationLabel(destination)}
        </div>
        <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
          {meta.blurb} {connected
            ? <span style={{ color: "#10B981", fontWeight: 600 }}> · Connected</span>
            : <span style={{ color: "#D97706", fontWeight: 600 }}> · Not connected</span>
          }
        </div>
      </div>
    </button>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 700, color: "var(--muted-foreground)",
      textTransform: "uppercase", letterSpacing: "0.05em",
      marginBottom: 6, margin: "0 0 6px 0",
    }}>
      {children}
    </p>
  );
}